import React, { useState } from 'react';
import { Button, ButtonSize, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { faFileSignature } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { YesNoDialog } from '@/components/dialogs';
import { useOptionConfigurator } from '../../OptionConfiguratorContext';
import { useConfiguratorMutations, SelectOptionParams } from '../../ConfiguratorMutationContext';

interface ImmersiveSignAndSendProps {
    /** Hard gate (unpicked force-selection style / expired deadline) — can't proceed at all. */
    disabled?: boolean;
    /** Impersonation view — signing is not allowed. */
    viewOnly?: boolean;
    size?: ButtonSize;
    /** Proceed into the real signing flow (navigates to sign-pre). */
    onProceed: () => void;
}

/**
 * Authenticated buyer "Sign and send" for the immersive configurator. Before signing it pre-checks
 * that every applicable option group for the current deadline has a selection. If some are still
 * pending, it offers a shorthand — apply each group's standard (default) option and then proceed —
 * or lets the buyer keep configuring. When nothing is pending it goes straight to signing.
 */
export const ImmersiveSignAndSend: React.FC<ImmersiveSignAndSendProps> = ({ disabled, viewOnly, size, onProceed }) => {
    const { configuratorData } = useOptionConfigurator();
    const { selectOptions, isSelecting } = useConfiguratorMutations();
    const [showDialog, setShowDialog] = useState(false);

    const groups = (configuratorData?.groups ?? []).filter((g) => g.applicable !== false);
    const unselected = groups.filter((g) => !(g.selectedOption ?? g.options.find((o) => o.selected)));

    if (viewOnly) {
        return (
            <SimpleTooltip placement="bottom" content={<Trans>Signing is disabled in this view</Trans>}>
                <Button disabled size={size} icon={faFileSignature}>
                    <Trans>Sign and send</Trans>
                </Button>
            </SimpleTooltip>
        );
    }

    const handleClick = () => {
        if (unselected.length === 0) {
            onProceed();
        } else {
            setShowDialog(true);
        }
    };

    const applyDefaultsAndProceed = async () => {
        const selections: SelectOptionParams[] = unselected.flatMap((g) => {
            const standard = g.options.find((o) => o.isDefault) ?? g.options[0];
            return standard
                ? [
                      {
                          option: standard,
                          customFieldData: standard.customFieldData ?? null,
                          roomId: g.roomId,
                          // Target each group's own deadline — in the multi-deadline preview the
                          // pending groups span several deadlines (no-op in the single-deadline flow).
                          deadlineId: g.deadlineId,
                      },
                  ]
                : [];
        });
        if (selections.length > 0) await selectOptions(selections);
        onProceed();
    };

    return (
        <>
            <Button
                disabled={disabled}
                loading={isSelecting}
                onClick={handleClick}
                size={size}
                icon={faFileSignature}>
                <Trans>Sign and send</Trans>
            </Button>

            <YesNoDialog
                show={showDialog}
                title={<Trans>Complete your selections</Trans>}
                body={
                    <Trans>
                        Some options for this deadline don't have a selection yet. Apply the standard selection for
                        the remaining options, or keep configuring?
                    </Trans>
                }
                onAccept={applyDefaultsAndProceed}
                onClose={() => setShowDialog(false)}
                confirmButtonText={<Trans>Apply standard selections</Trans>}
                cancelButtonText={<Trans>Continue configuring</Trans>}
            />
        </>
    );
};
